import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { BarChart3, ChevronLeft, MousePointerClick, UserMinus } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { useAuth } from '@/providers/AuthProvider'
import { EmptyState, PermissionDenied, Skeleton } from '@/components/States'
import { StatusBadge, type StatusKind } from '@/components/StatusBadge'
import {
  CAMP_STR as S,
  STATUS_KIND,
  fetchBroadcastsRaw,
  fetchEngagement,
  fetchUnsubscribes,
  type BroadcastLite,
  type EngagementReport,
  type UnsubRow,
} from './campaignsApi'

const BackLink = () => (
  <Link
    to="/broadcasts"
    className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
  >
    <ChevronLeft size={13} />
    {S.back}
  </Link>
)

const Tile = ({ label, value }: { label: string; value: string }) => (
  <div className="rounded-lg border border-border-default bg-surface-2 p-4">
    <div className="text-sm text-secondary">{label}</div>
    <div className="mt-1 text-2xl font-semibold text-primary">{value}</div>
  </div>
)

/**
 * A15.14 — Yetkazilish statistikasi. REAL /admin/broadcasts dan hosil qilinadi:
 * yuborilgan kampaniyalar, oluvchilar yig'indisi, holat taqsimoti.
 */
export function DeliveryStats() {
  const { can } = useAuth()
  const [rows, setRows] = useState<BroadcastLite[] | null>(null)
  const [tick, setTick] = useState(0)

  useEffect(() => {
    const ac = new AbortController()
    fetchBroadcastsRaw(ac.signal).then((r) => setRows(r))
    return () => ac.abort()
  }, [tick])

  const agg = useMemo(() => {
    const list = rows ?? []
    const sent = list.filter((r) => r.status === 'sent')
    const failed = list.filter((r) => r.status === 'failed')
    return {
      campaigns: list.length,
      sent: sent.length,
      recipients: sent.reduce((s, r) => s + (r.recipients || 0), 0),
      failed: failed.length,
    }
  }, [rows])

  if (!can(PERMISSIONS.USERS_VIEW)) return <PermissionDenied permission={PERMISSIONS.USERS_VIEW} />

  const sentRows = (rows ?? []).filter((r) => r.status === 'sent' || r.status === 'failed')

  return (
    <>
      <PageHeader
        title={S.deliveryTitle}
        screenId="A15.14"
        description={S.deliveryNote}
        actions={
          <div className="flex items-center gap-1.5">
            <Link
              to="/broadcasts/engagement"
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
            >
              <MousePointerClick size={13} />
              {S.engagementTitle}
            </Link>
            <button
              onClick={() => setTick((t) => t + 1)}
              className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
            >
              {S.refresh}
            </button>
            <BackLink />
          </div>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {rows === null ? (
          <div className="grid max-w-3xl gap-3 sm:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-24 w-full rounded-lg" />
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid max-w-3xl gap-3 sm:grid-cols-4">
              <Tile label={S.campaigns} value={agg.campaigns.toLocaleString()} />
              <Tile label={S.sentCount} value={agg.sent.toLocaleString()} />
              <Tile label={S.recipients} value={agg.recipients.toLocaleString()} />
              <Tile label={S.failedCount} value={agg.failed.toLocaleString()} />
            </div>
            {sentRows.length === 0 ? (
              <EmptyState icon={<BarChart3 size={26} strokeWidth={1.5} />} title={S.empty} />
            ) : (
              <div className="overflow-hidden rounded-lg border border-border-default">
                <table className="w-full text-sm">
                  <thead className="bg-surface-2 text-left text-xs uppercase tracking-wide text-tertiary">
                    <tr>
                      <th className="px-3 py-2 font-medium">{S.titleField}</th>
                      <th className="px-3 py-2 font-medium">{S.status}</th>
                      <th className="px-3 py-2 text-right font-medium">{S.recipients}</th>
                      <th className="px-3 py-2 font-medium">{S.sentAt}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-subtle">
                    {sentRows.map((r) => (
                      <tr key={r.id} className="bg-surface-1">
                        <td className="px-3 py-2 text-primary">{r.title || `#${r.id}`}</td>
                        <td className="px-3 py-2">
                          <StatusBadge status={(STATUS_KIND[r.status] as StatusKind) ?? 'pending'} />
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-primary">
                          {(r.recipients || 0).toLocaleString()}
                        </td>
                        <td className="px-3 py-2 text-tertiary">
                          {r.sent_at ? new Date(r.sent_at).toLocaleString() : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  )
}

/**
 * A15.15 — Yetkazish va qamrov. MyGram'da broadcast open/click event-trekingi yo'q,
 * shuning uchun soxta ochilish/bosish o'rniga REAL o'lchanadigan ma'lumot:
 * yuborilgan kampaniyalar oluvchilari + push-token deliverability (bu rassilka
 * umuman yetib boradimi). Hammasi jonli Postgres-agregat (/admin/broadcast-engagement).
 */
export function OpenClickStats() {
  const { can } = useAuth()
  const [rep, setRep] = useState<EngagementReport | null>(null)
  const [tick, setTick] = useState(0)

  useEffect(() => {
    const ac = new AbortController()
    fetchEngagement(ac.signal).then((r) => setRep(r))
    return () => ac.abort()
  }, [tick])

  if (!can(PERMISSIONS.USERS_VIEW)) return <PermissionDenied permission={PERMISSIONS.USERS_VIEW} />

  return (
    <>
      <PageHeader
        title={S.engagementTitle}
        screenId="A15.15"
        description={S.engagementNote}
        actions={
          <div className="flex items-center gap-1.5">
            <Link
              to="/broadcasts/delivery"
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
            >
              <BarChart3 size={13} />
              {S.deliveryTitle}
            </Link>
            <button
              onClick={() => setTick((t) => t + 1)}
              className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
            >
              {S.refresh}
            </button>
            <BackLink />
          </div>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {rep === null ? (
          <div className="grid max-w-3xl gap-3 sm:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-24 w-full rounded-lg" />
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid max-w-4xl gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Tile label={S.reachTotal} value={rep.total_recipients.toLocaleString()} />
              <Tile label={S.reachableNow} value={rep.fresh_tokens.toLocaleString()} />
              <Tile label={S.deliveryRate} value={`${rep.delivery_rate.toFixed(1)}%`} />
              <Tile label={S.deadTokens} value={rep.dead_tokens.toLocaleString()} />
            </div>
            <div className="grid max-w-4xl gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Tile label={S.pushTokensTotal} value={rep.push_tokens.toLocaleString()} />
              <Tile label={S.freshTokens} value={rep.fresh_tokens.toLocaleString()} />
              <Tile label={S.staleTokens} value={rep.stale_tokens.toLocaleString()} />
              <Tile label={S.sentCount} value={rep.sent_campaigns.toLocaleString()} />
            </div>
            {rep.rows.length === 0 ? (
              <EmptyState icon={<MousePointerClick size={26} strokeWidth={1.5} />} title={S.empty} />
            ) : (
              <div className="overflow-hidden rounded-lg border border-border-default">
                <table className="w-full text-sm">
                  <thead className="bg-surface-2 text-left text-xs uppercase tracking-wide text-tertiary">
                    <tr>
                      <th className="px-3 py-2 font-medium">{S.titleField}</th>
                      <th className="px-3 py-2 font-medium">{S.status}</th>
                      <th className="px-3 py-2 text-right font-medium">{S.recipients}</th>
                      <th className="px-3 py-2 text-right font-medium">{S.delivered}</th>
                      <th className="px-3 py-2 font-medium">{S.sentAt}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-subtle">
                    {rep.rows.map((r) => (
                      <tr key={r.id} className="bg-surface-1">
                        <td className="px-3 py-2 text-primary">{r.title || `#${r.id}`}</td>
                        <td className="px-3 py-2">
                          <StatusBadge status={(STATUS_KIND[r.status] as StatusKind) ?? 'pending'} />
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-primary">
                          {r.recipients.toLocaleString()}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-primary">
                          {r.delivered.toLocaleString()}
                        </td>
                        <td className="px-3 py-2 text-tertiary">
                          {r.sent_at ? new Date(r.sent_at).toLocaleString() : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  )
}

/**
 * A15.16 — Obunadan chiqishlar. Rassilkalarni o'chirgan foydalanuvchilar + sabab.
 * DORMANT (fail-soft bo'sh jadval).
 */
export function Unsubscribes() {
  const columns = useMemo<AdminColumn<UnsubRow>[]>(
    () => [
      { key: 'user', header: S.user, type: 'text', minWidth: 180 },
      {
        key: 'channel',
        header: S.channel,
        type: 'enum',
        width: 120,
        options: [
          { value: 'push', label: 'Push', status: 'review' },
          { value: 'email', label: 'Email', status: 'review' },
          { value: 'inapp', label: 'In-app', status: 'review' },
        ],
      },
      { key: 'reason', header: S.reason, type: 'longtext', minWidth: 220 },
      { key: 'created_at', header: S.when, type: 'date', width: 160 },
    ],
    [],
  )
  const fetcher = useCallback(fetchUnsubscribes, [])

  return (
    <>
      <PageHeader
        title={S.unsubTitle}
        screenId="A15.16"
        description={S.unsubNote}
        actions={
          <Link
            to="/broadcasts"
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {S.back}
          </Link>
        }
      />
      <DataTable<UnsubRow>
        screenId="A15.16"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        exportable
        emptyState={{ icon: <UserMinus size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
